import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { NoteHandler, NOTE_MAX_LENGTH } from "../../Apihander/Note/Note";
import { errorToast, successToast } from "../../utils/notificationUtils";
import { NoteEditorModal } from "../../pages/Notes/NotesModals";
import {
    ConfirmDialog, EMPTY_NOTE_TEXT, Ic, Spinner, fmt, newClientId, noteSeconds, readErrorMessage, toNote,
} from "../../pages/Notes/notesShared";
import "../../pages/Notes/notes.css";

export const NOTE_MARK_COLOR = "#FACC15";

const VISIBLE_NOTES = 2;
// How long a note stays highlighted once playback reaches it.
const HIGHLIGHT_SECONDS = 2;
// The list only follows playback once a recording has more notes than this.
const AUTO_SCROLL_MIN_NOTES = 2;

// Older records may only carry the single `note` field.
export const audioNotes = (audio) => audio?.notes || (audio?.note?._id ? [audio.note] : []);

// Timestamped notes on the selected recording; a new note is pinned to the playback time when "Add note" is pressed.
const AudioNote = ({ audio, currentTime, isPlaying, onSeek, onPause, onNotesChange }) => {
    const notes = audioNotes(audio);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const [listMaxHeight, setListMaxHeight] = useState(null);
    const [pendingTs, setPendingTs] = useState(null);
    const [text, setText] = useState("");
    const [adding, setAdding] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingNote, setDeletingNote] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // A different recording is a different set of notes, so drop anything half typed.
    useEffect(() => {
        setPendingTs(null);
        setText("");
        setEditingNote(null);
        setDeletingNote(null);
    }, [audio?._id]);

    useEffect(() => {
        if (pendingTs !== null) inputRef.current?.focus();
    }, [pendingTs]);

    // Rows wrap to different heights, so the list is cut at the bottom of the second one.
    useLayoutEffect(() => {
        const rows = listRef.current?.children;
        if (!rows || rows.length <= VISIBLE_NOTES) {
            setListMaxHeight(null);
            return;
        }
        const last = rows[VISIBLE_NOTES - 1];
        setListMaxHeight(last.offsetTop + last.offsetHeight);
    }, [notes]);

    const isNoteActive = (note) => {
        const at = noteSeconds(note);
        return isPlaying && currentTime >= at && currentTime < at + HIGHLIGHT_SECONDS;
    };
    const activeNoteIndex = notes.findIndex(isNoteActive);

    // Scrolls the list, not the page, so the note being played comes into view.
    useEffect(() => {
        if (notes.length <= AUTO_SCROLL_MIN_NOTES) return;
        const list = listRef.current;
        const row = list?.children[activeNoteIndex];
        if (row) list.scrollTo({ top: row.offsetTop, behavior: "smooth" });
    }, [activeNoteIndex, notes.length]);

    const openComposer = () => {
        onPause();
        setPendingTs(currentTime || 0);
    };

    const closeComposer = () => {
        setPendingTs(null);
        setText("");
    };

    const commitAddNote = async () => {
        const content = text.trim();
        if (adding) return;

        setAdding(true);
        try {
            const response = await NoteHandler.doCreateNote({
                audioId: audio._id,
                clientId: newClientId(),
                content,
                timestampMs: Math.round(pendingTs * 1000),
            });
            if (response?.statusCode === 200) {
                onNotesChange((prev) => [...prev, toNote(response.data)]);
                closeComposer();
                successToast("Note added");
            } else {
                errorToast(readErrorMessage(response));
            }
        } finally {
            setAdding(false);
        }
    };

    const saveEdit = async (content) => {
        setSavingEdit(true);
        try {
            const response = await NoteHandler.doUpdateNote(editingNote._id, { content });
            if (response?.statusCode === 200) {
                onNotesChange((prev) => prev.map((note) => (note._id === editingNote._id ? toNote(response.data) : note)));
                setEditingNote(null);
                successToast("Note updated");
            } else {
                errorToast(readErrorMessage(response));
            }
        } finally {
            setSavingEdit(false);
        }
    };

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            const response = await NoteHandler.doDeleteNote(deletingNote._id);
            if (response?.statusCode === 200) {
                onNotesChange((prev) => prev.filter((note) => note._id !== deletingNote._id));
                setDeletingNote(null);
                successToast("Note deleted");
            } else {
                errorToast(readErrorMessage(response));
            }
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="nt vr-card vr-rd-notes">
            <div className={`vr-rd-notes-head${notes.length || pendingTs !== null ? " has-body" : ""}`}>
                <span className="vr-rd-notes-title">
                    <span className="vr-icon-tile vr-rd-notes-tile"><Ic name="note" size="s" /></span>
                    {notes.length ? `Notes · ${notes.length}` : "Notes"}
                </span>
                {pendingTs === null && (
                    <button type="button" className="vr-btn vr-btn-secondary vr-btn-sm vr-rd-notes-add" onClick={openComposer}>
                        <Ic name="noteplus" size="s" />
                        {`Add note`}
                    </button>
                )}
            </div>

            {pendingTs !== null && (
                <div className={`vr-rd-composer${notes.length ? " has-list" : ""}`}>
                    <div className="vr-rd-composer-meta">
                        <span>
                            New note at <b>{fmt(pendingTs)}</b>
                        </span>
                        <span className="vr-rd-count">{`${text.length}/${NOTE_MAX_LENGTH}`}</span>
                    </div>
                    <div className="nt-noteinput">
                        <input
                            ref={inputRef}
                            className="vr-input"
                            placeholder="Type your note"
                            value={text}
                            maxLength={NOTE_MAX_LENGTH}
                            disabled={adding}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") commitAddNote();
                                if (e.key === "Escape") closeComposer();
                            }}
                        />
                        <button type="button" className="vr-btn vr-btn-primary vr-btn-sm" disabled={adding} onClick={commitAddNote}>
                            {adding ? <Spinner /> : "Add"}
                        </button>
                        <button type="button" className="vr-btn vr-btn-ghost vr-btn-sm" disabled={adding} onClick={closeComposer}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {notes.length ? (
                <div ref={listRef} className="vr-rd-notelist" style={{ maxHeight: listMaxHeight ?? "none" }}>
                    {notes.map((note) => {
                        const at = noteSeconds(note);
                        const active = isNoteActive(note);
                        return (
                        <div className={`vr-rd-note${active ? " is-active" : ""}`} key={note._id}>
                            <button
                                type="button"
                                className="vr-rd-ts"
                                title="Play from here"
                                onClick={() => onSeek(at)}
                            >
                                {fmt(at)}
                            </button>
                            <span className="nt-txt">{note.content || EMPTY_NOTE_TEXT}</span>
                            <span className="vr-rd-acts">
                                <button type="button" className="vr-iconbtn" title="Edit" onClick={() => setEditingNote(note)}><Ic name="edit" size="s" /></button>
                                <button type="button" className="vr-iconbtn is-danger" title="Delete" onClick={() => setDeletingNote(note)}><Ic name="trash" size="s" /></button>
                            </span>
                        </div>
                        );
                    })}
                </div>
            ) : pendingTs === null && (
                <p className="vr-rd-empty-notes">
                    <Ic name="pin" size="s" />
                    No notes yet. Play the recording and add a note at any moment.
                </p>
            )}

            {editingNote && (
                <NoteEditorModal
                    timestampSec={noteSeconds(editingNote)}
                    initialValue={editingNote.content}
                    saving={savingEdit}
                    onClose={() => setEditingNote(null)}
                    onSave={saveEdit}
                />
            )}
            {deletingNote && (
                <ConfirmDialog
                    title="Delete note?"
                    message="This note will be permanently deleted."
                    busy={deleting}
                    onCancel={() => setDeletingNote(null)}
                    onConfirm={confirmDelete}
                />
            )}
        </div>
    );
};

export default AudioNote;
